import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  searchQuestionWithTerm,
  searchProfileWithTerm,
  searchProductWithTerm,
  searchOpinionWithTerm,
  resetQuestionResult,
  resetProfileResult,
  resetProductResult,
} from 'app/dispatcher/searchDispatcher';
import { SEARCH_TYPE, SEARCH_PAGE_OPTIONS } from 'app/constants/search';
import get from 'lodash/get';
import { XCircle } from 'react-feather';
import debounce from 'lodash/debounce';
import { getCategoryNameAndId } from 'app/selectors/productSelector';
import { useSelectGroupList } from 'entity/group/groupHooks';
import Fuse from 'fuse.js';
import mixpanel, { trackedEvents } from 'app/utils/mixpanel';
import SearchFilter from './SearchFilter';
import * as S from './styles';
import ListBySearchType from './ListBySearchType';

const SearchContent = props => {
  const { searchTerm: initialSearchTerm } = props;

  // Selectors
  const categoryList = useSelector(getCategoryNameAndId);
  const groups = useSelectGroupList();

  const dispatch = useDispatch();

  // Amend keyword for travel insurance
  const amendedList = categoryList.map(category => {
    let keyword = '';
    if (
      category.name === 'Credit Cards' ||
      category.name === 'Travel Insurance'
    ) {
      keyword = 'best';
    }
    return {
      ...category,
      keyword,
    };
  });

  // Refs
  const hasMounted = useRef(false);
  const productFuse = useRef(new Fuse(amendedList, SEARCH_PAGE_OPTIONS));

  let initialProductCategoryResult = [];
  if (initialSearchTerm && initialSearchTerm.length > 0) {
    initialProductCategoryResult = productFuse.current.search(
      initialSearchTerm.trim(),
    );
  }

  // State
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm);
  const [selectedFilter, setSelectedFilter] = useState(SEARCH_TYPE.ALL);
  const [productCategoryResult, setProductCategoryResult] = useState(
    initialProductCategoryResult,
  );
  const [groupResult, setGroupResult] = useState([]);

  const handleCategorySearch = () => {
    if (!searchTerm) return;
    const result = productFuse.current.search(searchTerm.trim());
    setProductCategoryResult(result);
  };

  const handleGroupSearch = () => {
    if (!searchTerm) return;
    const result = groups.filter(group => {
      return group.name.toLowerCase().includes(searchTerm.toLowerCase());
    });
    setGroupResult(result);
  };

  const handleResetResult = () => {
    dispatch(resetQuestionResult());
    dispatch(resetProfileResult());
    dispatch(resetProductResult());
    setProductCategoryResult([]);
  };

  const onSearchChange = debounce(() => {
    const term = searchTerm;

    if (get(term, 'length', 0) === 0) handleResetResult();
    if (!term || term.trim().length < 2) return;

    handleCategorySearch();
    handleGroupSearch();

    if (selectedFilter === SEARCH_TYPE.ALL) {
      dispatch(searchQuestionWithTerm({ term }));
      dispatch(searchProductWithTerm({ term }));
      dispatch(searchOpinionWithTerm({ term }));
      dispatch(searchProfileWithTerm({ term }));
    }

    const params = { term, perPage: 8 };
    if (selectedFilter === SEARCH_TYPE.QUESTION) {
      dispatch(searchQuestionWithTerm(params));
    }
    if (selectedFilter === SEARCH_TYPE.PRODUCT) {
      dispatch(searchProductWithTerm(params));
    }
    if (selectedFilter === SEARCH_TYPE.OPINION) {
      dispatch(searchOpinionWithTerm(params));
    }
    if (selectedFilter === SEARCH_TYPE.PROFILE) {
      dispatch(searchProfileWithTerm(params));
    }
  }, 800);

  // Effects
  useEffect(() => {
    if (hasMounted.current) {
      onSearchChange();
    } else {
      hasMounted.current = true;
    }
  }, [selectedFilter]);

  useEffect(() => {
    if (hasMounted.current) {
      switch (searchTerm) {
        case '':
          handleResetResult();
          break;
        default:
          onSearchChange();
          break;
      }
    } else {
      hasMounted.current = true;
    }
  }, [searchTerm]);

  return (
    <S.PageContent>
      <S.TopSection>
        <S.ContentWrapper>
          <S.InputContainer>
            {Boolean(searchTerm) && (
              <S.ClearButton onClick={() => setSearchTerm('')}>
                <XCircle size={24} />
              </S.ClearButton>
            )}
            <S.Input
              onChange={e => setSearchTerm(e.target.value)}
              onFocus={() => {
                mixpanel.track(trackedEvents.Search);
              }}
              value={searchTerm}
              maxLength="50"
              placeholder="Search here"
            />
          </S.InputContainer>
          <SearchFilter
            selectedFilter={selectedFilter}
            onSelect={filter => setSelectedFilter(filter)}
          />
        </S.ContentWrapper>
      </S.TopSection>
      {Boolean(searchTerm) && (
        <S.BottomSection>
          <ListBySearchType
            productCategoryResult={productCategoryResult}
            groupResult={groupResult}
            searchTerm={searchTerm}
            selectedFilter={selectedFilter}
            setSelectedFilter={setSelectedFilter}
          />
        </S.BottomSection>
      )}
    </S.PageContent>
  );
};

export default SearchContent;
